/**
 * Hook React pour gérer la connexion à une balance
 * Supporte plusieurs modèles via l'architecture ScaleAdapter
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ScaleAdapter,
  WeightReading,
  ScaleConfig,
  ScaleAdapterFactory,
  ManualScaleAdapter,
  BaxtranSLAdapter,
} from '../services/scales';
import { useTenant } from '../contexts/TenantContext';
import { logger } from '../utils/logger';

export interface UseScaleState {
  // État de la connexion
  connected: boolean;
  connecting: boolean;
  error: string | null;

  // Dernière lecture de poids
  currentWeight: WeightReading | null;

  // Informations adapter
  adapterName: string;
  isManualMode: boolean;
  // true si l'adapter peut se connecter sans geste utilisateur (WebSocket bridge / manuel).
  // false pour Web Serial (BAXTRAN_SL sur Chrome) qui exige un clic par spec navigateur.
  canAutoConnect: boolean;

  // Actions
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  tare: () => Promise<void>;
  readWeight: () => Promise<WeightReading>;

  // Pour mode manuel uniquement
  setManualWeight?: (weightGrams: number) => void;
}

/**
 * Hook pour gérer une balance
 */
export function useScale(): UseScaleState {
  const { tenant } = useTenant();
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentWeight, setCurrentWeight] = useState<WeightReading | null>(null);

  const adapterRef = useRef<ScaleAdapter | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const adapterInitializedRef = useRef(false);

  // Créer l'adapter UNE SEULE FOIS au premier montage
  // IMPORTANT: Ne pas dépendre de `tenant` pour éviter les re-créations multiples
  useEffect(() => {
    // Si déjà initialisé, ne rien faire
    if (adapterInitializedRef.current) {
      logger.log('[useScale] Adapter déjà initialisé, skip');
      return;
    }

    logger.log('[useScale] Initialisation de l\'adapter balance');

    // Configuration dynamique depuis le tenant ou localStorage (pour multi-POS)
    const hardwareConfig = tenant?.config?.hardware?.scale;
    const localPort = localStorage.getItem('scale_port');

    // Déterminer le modèle : tenant config > défaut BAXTRAN_SL
    const model = (hardwareConfig?.type as ScaleConfig['model']) || 'BAXTRAN_SL';

    // Déterminer le port : tenant config > localStorage > undefined (auto-detect)
    const port = hardwareConfig?.settings?.port || localPort || undefined;

    // Si balance désactivée explicitement, utiliser mode manuel
    if (hardwareConfig && hardwareConfig.enabled === false) {
      logger.log('[useScale] Balance désactivée dans la config, mode manuel');
      const config: ScaleConfig = { model: 'MANUAL' };
      adapterRef.current = ScaleAdapterFactory.create(config);
      adapterInitializedRef.current = true;
      return;
    }

    const config: ScaleConfig = {
      model,
      autoConnect: false, // Web Serial API nécessite action utilisateur
      port,
    };

    logger.log('[useScale] Config balance:', { model, port: port || 'auto-detect' });
    adapterRef.current = ScaleAdapterFactory.create(config);
    adapterInitializedRef.current = true;

    // Note: Web Serial API nécessite une action utilisateur (clic sur bouton)
    // L'auto-connect n'est donc pas possible au chargement de la page

    // IMPORTANT: Pas de cleanup ici car le ScaleWidget est maintenant global
    // et doit rester connecté en permanence, même lors des changements de vendeur
  }, []); // Array vide = exécution UNE SEULE FOIS au montage

  /**
   * Connecter la balance
   */
  const connect = useCallback(async () => {
    if (!adapterRef.current) {
      setError('Aucun adapter de balance disponible');
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      await adapterRef.current.connect();

      // S'abonner aux changements de poids
      unsubscribeRef.current = adapterRef.current.subscribe((reading) => {
        setCurrentWeight(reading);
      });

      setConnected(true);
      setConnecting(false);
      logger.log('[useScale] Connecté avec succès');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur de connexion';
      setError(errorMessage);
      setConnected(false);
      setConnecting(false);
      logger.error('[useScale] Erreur connexion:', err);
    }
  }, []);

  /**
   * Déconnecter la balance
   */
  const disconnect = useCallback(async () => {
    if (!adapterRef.current) return;

    try {
      // Désabonner
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      await adapterRef.current.disconnect();
      setConnected(false);
      setCurrentWeight(null);
      setError(null);
      logger.log('[useScale] Déconnecté');
    } catch (err) {
      logger.error('[useScale] Erreur déconnexion:', err);
    }
  }, []);

  /**
   * Tarrer la balance
   */
  const tare = useCallback(async () => {
    if (!adapterRef.current) {
      throw new Error('Aucun adapter de balance disponible');
    }

    if (!adapterRef.current.isConnected()) {
      throw new Error('Balance non connectée');
    }

    try {
      await adapterRef.current.tare();
      logger.log('[useScale] TARE effectué');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur TARE';
      setError(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Lire le poids actuel
   */
  const readWeight = useCallback(async (): Promise<WeightReading> => {
    if (!adapterRef.current) {
      throw new Error('Aucun adapter de balance disponible');
    }

    if (!adapterRef.current.isConnected()) {
      throw new Error('Balance non connectée');
    }

    try {
      const reading = await adapterRef.current.readWeight();
      setCurrentWeight(reading);
      return reading;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lecture poids';
      setError(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Définir un poids manuellement (mode manuel uniquement)
   */
  const setManualWeight = useCallback((weightGrams: number) => {
    if (!adapterRef.current || !(adapterRef.current instanceof ManualScaleAdapter)) {
      logger.warn('[useScale] setManualWeight appelé mais adapter n\'est pas ManualScaleAdapter');
      return;
    }

    adapterRef.current.setWeight(weightGrams);
  }, []);

  // Surveillance périodique de l'état de connexion de l'adapter
  // Nécessaire car le WebSocket adapter peut se déconnecter/reconnecter en interne
  // (ex: perte réseau 4G au marché) sans que le hook React le sache
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!adapterRef.current) return;

    const checkConnection = () => {
      const adapter = adapterRef.current;
      if (!adapter) return;

      const adapterConnected = adapter.isConnected();

      // Déconnexion détectée
      if (wasConnectedRef.current && !adapterConnected) {
        logger.warn('[useScale] Perte de connexion détectée par polling');
        setConnected(false);
        wasConnectedRef.current = false;
      }

      // Reconnexion détectée (adapter reconnecté en interne par Socket.IO)
      if (!wasConnectedRef.current && adapterConnected && connected === false) {
        logger.log('[useScale] Reconnexion détectée par polling');
        setConnected(true);
        setError(null); // La balance est revenue (Socket.IO a reconnecté) → effacer le
                        // message d'erreur périmé, sinon il reste affiché à tort en pleine
                        // session (les vendeurs croient la balance HS alors qu'elle marche).
        wasConnectedRef.current = true;

        // Ré-abonner aux poids si pas déjà fait
        if (!unsubscribeRef.current) {
          unsubscribeRef.current = adapter.subscribe((reading) => {
            setCurrentWeight(reading);
          });
        }
      }
    };

    // Vérifier toutes les 3 secondes
    const interval = setInterval(checkConnection, 3000);
    return () => clearInterval(interval);
  }, [connected]);

  // Garder wasConnectedRef synchronisé avec le state
  useEffect(() => {
    wasConnectedRef.current = connected;
  }, [connected]);

  const isManualMode = adapterRef.current instanceof ManualScaleAdapter;
  // Web Serial (BaxtranSLAdapter) exige un geste utilisateur → pas d'auto-connexion.
  // WebSocket bridge (Safari/marché) et mode manuel : auto-connexion possible.
  const canAutoConnect = !!adapterRef.current && !(adapterRef.current instanceof BaxtranSLAdapter);

  return {
    connected,
    connecting,
    error,
    currentWeight,
    adapterName: adapterRef.current?.modelName ?? 'Non configuré',
    isManualMode,
    canAutoConnect,
    connect,
    disconnect,
    tare,
    readWeight,
    setManualWeight: isManualMode ? setManualWeight : undefined,
  };
}
